import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';

const ColorSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ResetButton = styled.button`
  align-self: flex-start;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ColorRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ColorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ColorLabel = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
`;

const CompactColorControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const ColorPreview = styled.div`
  width: 32px;
  height: 32px;
  background: ${({ color }) => color};
  border-radius: 6px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.05);
    border-color: rgba(255, 255, 255, 0.4);
  }

  /* Checkerboard pattern for transparency */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(45deg, #666 25%, transparent 25%),
      linear-gradient(-45deg, #666 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #666 75%),
      linear-gradient(-45deg, transparent 75%, #666 75%);
    background-size: 6px 6px;
    background-position:
      0 0,
      0 3px,
      3px -3px,
      -3px 0px;
    z-index: -1;
  }
`;

const QuickSwatches = styled.div`
  display: flex;
  gap: 0.375rem;
`;

const SwatchButton = styled.button`
  width: 24px;
  height: 24px;
  background: ${({ color }) =>
    color === 'transparent' ? 'transparent' : color};
  border: 1.5px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: scale(1.15);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:active {
    transform: scale(0.95);
  }

  /* Checkerboard for white and transparent */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(45deg, #666 25%, transparent 25%),
      linear-gradient(-45deg, #666 25%, transparent 25%),
      linear-gradient(45deg, transparent 75%, #666 75%),
      linear-gradient(-45deg, transparent 75%, #666 75%);
    background-size: 4px 4px;
    background-position:
      0 0,
      0 2px,
      2px -2px,
      -2px 0px;
    z-index: -1;
    border-radius: 2px;
  }
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ColorInput = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.375rem 0.625rem;
  color: white;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  font-size: 0.8rem;
  font-weight: 500;
  width: 90px;
  text-transform: uppercase;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
    text-transform: none;
  }

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.2);
  }
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.9);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.3s ease;
    transform: ${(props) =>
      props.expanded ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const AdvancedSection = styled.div`
  overflow: hidden;
  transition: all 0.3s ease;
  max-height: ${(props) => (props.expanded ? '300px' : '0')};
  opacity: ${(props) => (props.expanded ? '1' : '0')};
`;

const AdvancedContent = styled.div`
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  margin-top: 0.75rem;
`;

const ColorPickerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 1rem;
`;

const SaturationLightness = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    #fff,
    ${(props) => `hsl(${props.hue}, 100%, 50%)`}
  );
  cursor: crosshair;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, transparent, #000);
  }
`;

const SaturationPointer = styled.div`
  position: absolute;
  width: 12px;
  height: 12px;
  border: 2px solid white;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.3);
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const HueSlider = styled.div`
  position: relative;
  width: 100%;
  height: 12px;
  background: linear-gradient(
    to right,
    #ff0000 0%,
    #ffff00 17%,
    #00ff00 33%,
    #00ffff 50%,
    #0000ff 67%,
    #ff00ff 83%,
    #ff0000 100%
  );
  border-radius: 6px;
  cursor: pointer;
  margin: 0.75rem 0;
`;

const HuePointer = styled.div`
  position: absolute;
  width: 16px;
  height: 16px;
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const ColorInputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InputLabel = styled.label`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const SmallInput = styled.input`
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 4px;
  padding: 0.375rem 0.5rem;
  color: white;
  font-size: 0.75rem;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    background: rgba(255, 255, 255, 0.1);
  }
`;

// Utility functions
const hexToHsl = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return { h: 0, s: 0, l: 0 };

  let r = parseInt(result[1], 16) / 255;
  let g = parseInt(result[2], 16) / 255;
  let b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
};

const hslToHex = (h, s, l) => {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : { r: 0, g: 0, b: 0 };
};

const rgbToHex = (r, g, b) => {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const ColorPicker = ({ label, value, onChange, presetColors }) => {
  const [hexInput, setHexInput] = useState(
    value === 'transparent' ? '' : value.replace('#', ''),
  );
  const [expanded, setExpanded] = useState(false);
  const [hsl, setHsl] = useState(
    value === 'transparent' ? { h: 0, s: 0, l: 100 } : hexToHsl(value),
  );
  const [rgb, setRgb] = useState(
    value === 'transparent' ? { r: 255, g: 255, b: 255 } : hexToRgb(value),
  );
  const saturationRef = useRef(null);
  const hueRef = useRef(null);
  const [isDraggingSaturation, setIsDraggingSaturation] = useState(false);
  const [isDraggingHue, setIsDraggingHue] = useState(false);

  useEffect(() => {
    if (value === 'transparent') {
      setHexInput('');
      setHsl({ h: 0, s: 0, l: 100 });
      setRgb({ r: 255, g: 255, b: 255 });
    } else {
      setHexInput(value.replace('#', ''));
      setHsl(hexToHsl(value));
      setRgb(hexToRgb(value));
    }
  }, [value]);

  const handleHexChange = (e) => {
    const hex = e.target.value.replace('#', '').toUpperCase();
    setHexInput(hex);
    if (/^[0-9A-Fa-f]{6}$/.test(hex)) {
      onChange(`#${hex}`);
    } else if (hex === 'TRANSPARENT' || hex === '') {
      onChange('transparent');
    }
  };

  const handleRgbChange = (channel, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue >= 0 && numValue <= 255) {
      const newRgb = { ...rgb, [channel]: numValue };
      const hex = rgbToHex(newRgb.r, newRgb.g, newRgb.b);
      onChange(hex);
    }
  };

  const handleSaturationMouseDown = (e) => {
    setIsDraggingSaturation(true);
    updateSaturationLightness(e);
  };

  const handleHueMouseDown = (e) => {
    setIsDraggingHue(true);
    updateHue(e);
  };

  const updateSaturationLightness = (e) => {
    if (!saturationRef.current) return;
    const rect = saturationRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

    const s = Math.round(x * 100);
    const l = Math.round((1 - y) * 100);
    const newHsl = { ...hsl, s, l };
    setHsl(newHsl);
    onChange(hslToHex(newHsl.h, newHsl.s, newHsl.l));
  };

  const updateHue = (e) => {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const h = Math.round(x * 360);
    const newHsl = { ...hsl, h };
    setHsl(newHsl);
    onChange(hslToHex(newHsl.h, newHsl.s, newHsl.l));
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingSaturation) updateSaturationLightness(e);
      if (isDraggingHue) updateHue(e);
    };

    const handleMouseUp = () => {
      setIsDraggingSaturation(false);
      setIsDraggingHue(false);
    };

    if (isDraggingSaturation || isDraggingHue) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingSaturation, isDraggingHue, hsl]);

  return (
    <ColorRow>
      <ColorHeader>
        <ColorLabel>{label}</ColorLabel>
        <ExpandButton
          expanded={expanded}
          onClick={() => setExpanded(!expanded)}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 11.5l-5-5 1.5-1.5L8 8.5 11.5 5l1.5 1.5z" />
          </svg>
        </ExpandButton>
      </ColorHeader>

      <CompactColorControl>
        <ColorPreview
          color={value === 'transparent' ? 'transparent' : value}
          onClick={() => setExpanded(!expanded)}
        />
        <QuickSwatches>
          {presetColors.map((color) => (
            <SwatchButton
              key={color}
              color={color}
              onClick={() => onChange(color)}
              title={color}
            />
          ))}
        </QuickSwatches>
        <InputWrapper>
          <span
            style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem' }}
          >
            #
          </span>
          <ColorInput
            type="text"
            value={hexInput}
            onChange={handleHexChange}
            placeholder={
              label.includes('Background') ? 'transparent' : '000000'
            }
            maxLength={11}
          />
        </InputWrapper>
      </CompactColorControl>

      <AdvancedSection expanded={expanded}>
        <AdvancedContent>
          <ColorPickerWrapper>
            <SaturationLightness
              ref={saturationRef}
              hue={hsl.h}
              onMouseDown={handleSaturationMouseDown}
            >
              <SaturationPointer
                style={{
                  left: `${hsl.s}%`,
                  top: `${100 - hsl.l}%`,
                }}
              />
            </SaturationLightness>
          </ColorPickerWrapper>

          <HueSlider ref={hueRef} onMouseDown={handleHueMouseDown}>
            <HuePointer style={{ left: `${(hsl.h / 360) * 100}%` }} />
          </HueSlider>

          <ColorInputGrid>
            <InputGroup>
              <InputLabel>R</InputLabel>
              <SmallInput
                type="number"
                min="0"
                max="255"
                value={rgb.r}
                onChange={(e) => handleRgbChange('r', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <InputLabel>G</InputLabel>
              <SmallInput
                type="number"
                min="0"
                max="255"
                value={rgb.g}
                onChange={(e) => handleRgbChange('g', e.target.value)}
              />
            </InputGroup>
            <InputGroup>
              <InputLabel>B</InputLabel>
              <SmallInput
                type="number"
                min="0"
                max="255"
                value={rgb.b}
                onChange={(e) => handleRgbChange('b', e.target.value)}
              />
            </InputGroup>
          </ColorInputGrid>
        </AdvancedContent>
      </AdvancedSection>
    </ColorRow>
  );
};

const QRColorPicker = ({
  foregroundColor = '#000000',
  backgroundColor = '#ffffff',
  onForegroundChange,
  onBackgroundChange,
}) => {
  const foregroundPresets = ['#000000', '#666666', '#7c3aed'];
  const backgroundPresets = ['#ffffff', '#f5f5f5', 'transparent'];

  const handleReset = () => {
    onForegroundChange('#000000');
    onBackgroundChange('#ffffff');
  };

  return (
    <ColorSection>
      <ColorPicker
        label="Foreground (QR Pattern)"
        value={foregroundColor}
        onChange={onForegroundChange}
        presetColors={foregroundPresets}
      />
      <ColorPicker
        label="Background"
        value={backgroundColor}
        onChange={onBackgroundChange}
        presetColors={backgroundPresets}
      />
      <ResetButton onClick={handleReset}>
        <svg viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
          <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
        </svg>
        Reset to B&W
      </ResetButton>
    </ColorSection>
  );
};

export default QRColorPicker;
